import { ReactNode } from 'react';
import RichText from '#components/common/richText/RichText';
import TruncatableAttribute from '#components/common/mediaAttributes/TruncatableAttribute';
import Tabs from '#components/navigation/tabs/Tabs';
import SpecialTeaser from '#components/teasers/special-teaser/SpecialTeaser';
import HorizontalScrollList from '#components/lists/horizontal-scroll-list/HorizontalScrollList';
import { useOvermindActions } from '#store/overmind';
import PageSection from '#components/sections/page-section/PageSection';
import { Media } from '#types/generated/graphql-types';
import useTranslation from 'next-translate/useTranslation';
import { getPlayerInstance, handlePlayerPlay } from '#utils/player/controls';
import {
  translateLanguageCodes,
  formatAttributeDate,
  getSeriesEpisodeAttributeValue,
  getStudioAttributeValue,
} from '#utils/mediaAttributes';

type Props = {
  extras: Media[];
  mediaDescription: string;
  media: Media;
  isEpisode?: boolean;
};

const HeroTabs = ({
  extras,
  mediaDescription,
  media,
  isEpisode = false,
}: Props) => {
  const { t } = useTranslation();

  const attributeList =
    media.attributes?.map((attr) => ({
      label: attr?.name,
      valueList: attr?.values?.filter(Boolean) as string[] | undefined,
      isSearchable: attr?.isSearchable ?? false,
    })) ?? [];

  const mediaAttributes: Array<{
    label?: string | null;
    value?: ReactNode;
    valueList?: string[];
    isSearchable?: boolean;
  }> = [
    {
      label: t('qualityInfo'),
      value: media.availableQualities?.join(', ') || media.mediaQuality,
    },
    {
      label: t('attributeAgeRating'),
      value: media.fskRating === '18' ? media.fskRating : undefined,
    },
    {
      label: t('attributeMediaLanguages'),
      value: translateLanguageCodes(media.mediaLanguages, t),
    },
    {
      label: t('subtitles'),
      value: translateLanguageCodes(media.subtitleLanguages, t),
    },
    {
      label: t('filterCategory'),
      value: media.categories?.map((c) => c.title).join(', '),
    },
    { label: t('attributeTheme'), value: media.theme?.title },
    {
      label: t('attributeType'),
      value: getSeriesEpisodeAttributeValue(media, t, isEpisode),
    },
    {
      label: t('attributeLiveDate'),
      value: formatAttributeDate(media.liveDate),
    },
    {
      label: t('attributeKillDate'),
      value: formatAttributeDate(media.killDate),
    },
    { label: t('attributeProductionYear'), value: media.year },
    { label: t('gridTabStudios'), value: getStudioAttributeValue(media, t) },
    ...attributeList,
  ];

  const {
    player: { initPlayer },
  } = useOvermindActions();

  const renderSliderItem = (item) => {
    return (
      <SpecialTeaser
        key={item.id}
        teaserDataRaw={item}
        disableLikeButton={true}
        disableLinkRedirection={true}
        onClick={(event) => {
          event.stopPropagation();
          initPlayer({ iri: item.iri });

          const player = getPlayerInstance();

          if (player && player.paused()) {
            handlePlayerPlay(player);
          }
        }}
      />
    );
  };

  const renderTabs = () => {
    const tabItems = [];
    if (extras?.length > 0) {
      tabItems.push({
        title: t('Extras'),
        component: (
          <HorizontalScrollList
            direction="landscape"
            items={extras}
            renderItem={renderSliderItem}
          />
        ),
      });
    }

    if (mediaDescription || mediaAttributes.some((attr) => attr.value)) {
      tabItems.push({
        title: t('Details'),
        component: (
          <div className="tw-flex tw-flex-col xlarge-up:tw-flex-row xlarge-up:tw-gap-8 tw-w-full">
            <div className="tw-flex-1 tw-min-w-0">
              {mediaDescription && <RichText text={mediaDescription} />}
            </div>
            <div className="tw-flex-1 tw-min-w-0 tw-flex xlarge-up:tw-justify-end tw-text-medium tw-leading-[1.4] tw-font-body text-[var(--color-default-text)]">
              <div className="tw-flex tw-flex-col tw-gap-y-2 tw-w-full xlarge-up:tw-w-2/3">
                {mediaAttributes
                  .map((item, index) => ({
                    ...item,
                    attributeKey: `media-attribute-${index}`,
                  }))
                  .filter((item) => item.value || item.valueList?.length)
                  .map(
                    ({
                      attributeKey,
                      label,
                      value,
                      valueList,
                      isSearchable,
                    }) => (
                      <div
                        key={attributeKey}
                        className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-2"
                      >
                        <span className="tw-whitespace-nowrap tw-font-bold">
                          {label}:
                        </span>
                        <span className="tw-flex-1 tw-break-words tw-min-w-fit tw-relative tw-leading-[1.5]">
                          {valueList && valueList.length > 0 ? (
                            <TruncatableAttribute
                              values={valueList}
                              t={t}
                              isSearchable={isSearchable}
                            />
                          ) : (
                            value
                          )}
                        </span>
                      </div>
                    )
                  )}
              </div>
            </div>
          </div>
        ),
      });
    }
    return <Tabs tabItems={tabItems} />;
  };

  return (
    <PageSection>
      <div className="medium-down:tw-pt-1 large-up:tw-pt-7">{renderTabs()}</div>
    </PageSection>
  );
};

export default HeroTabs;
